import React, { useState } from 'react';
import { X, Save, CheckCircle2 } from 'lucide-react';
import { PAYMENT_METHODS } from '../../mock';

const STATUSES = ['Pending', 'Paid', 'Partial', 'Refunded'];
const inputCls = 'w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2 text-sm text-cbtu-cream outline-none';

export const totalDue = (r) => Number(r.total_paid ?? r.fee ?? 0);
export const amountReceived = (r) => (r.amount_received != null ? Number(r.amount_received) : (r.payment_status === 'Paid' ? totalDue(r) : 0));

const autoStatus = (status, due, received) => {
  if (status === 'Refunded') return status;
  if (due > 0 && received >= due) return 'Paid';
  if (received > 0) return 'Partial';
  return 'Pending';
};

function Field({ label, children }) {
  return <label className="block"><span className="block text-[10px] tracking-widest text-gold font-heading mb-1">{label}</span>{children}</label>;
}

export const EditPaymentModal = ({ record, onClose, onSave, saving }) => {
  const [f, setF] = useState({
    total_paid: totalDue(record),
    amount_received: amountReceived(record),
    payment_status: record.payment_status || 'Pending',
    payment_method: record.payment_method || '',
    notes: record.notes || '',
  });
  const due = Number(f.total_paid) || 0;
  const received = Number(f.amount_received) || 0;
  const balance = due - received;
  const setMoney = (k, v) => {
    const next = { ...f, [k]: v };
    setF({ ...next, payment_status: autoStatus(f.payment_status, Number(next.total_paid) || 0, Number(next.amount_received) || 0) });
  };
  const methods = PAYMENT_METHODS.includes(f.payment_method) || !f.payment_method ? PAYMENT_METHODS : [f.payment_method, ...PAYMENT_METHODS];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" role="dialog" aria-modal="true" data-testid="edit-payment-modal">
      <div className="w-full max-w-lg card-dark rounded-md border border-gold/40 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gold/30 bg-cbtu-green/40">
          <div>
            <h3 className="font-heading tracking-widest text-gold text-sm">UPDATE PAYMENT</h3>
            <div className="text-cbtu-cream text-sm mt-0.5">{record.name} · <span className="text-cbtu-muted">{record.category}</span></div>
          </div>
          <button onClick={onClose} className="text-cbtu-muted hover:text-gold" aria-label="Close" data-testid="edit-payment-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="TOTAL DUE $"><input data-testid="edit-total-due" type="number" min="0" value={f.total_paid} onChange={(e) => setMoney('total_paid', e.target.value)} className={inputCls} /></Field>
            <Field label="AMOUNT RECEIVED $"><input data-testid="edit-amount-received" type="number" min="0" value={f.amount_received} onChange={(e) => setMoney('amount_received', e.target.value)} className={inputCls} /></Field>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-sm border border-gold/20 bg-cbtu-black/60 px-4 py-3">
            <div className="text-sm text-cbtu-cream">Balance due: <span className={`font-display text-xl ml-1 ${balance > 0 ? 'text-orange-300' : 'text-green-300'}`} data-testid="edit-balance">${balance}</span></div>
            <button type="button" onClick={() => setF({ ...f, amount_received: due, payment_status: 'Paid' })} data-testid="edit-mark-paid-btn" className="bg-[#2ca01c] hover:bg-[#248a16] text-white font-heading text-[11px] tracking-widest px-3 py-2 rounded-sm inline-flex items-center gap-1.5 transition-colors">
              <CheckCircle2 size={13} /> MARK PAID IN FULL
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="STATUS">
              <select data-testid="edit-status" value={f.payment_status} onChange={(e) => setF({ ...f, payment_status: e.target.value })} className={inputCls}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="PAYMENT METHOD">
              <select data-testid="edit-method" value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })} className={inputCls}>
                <option value="">— Set —</option>
                {methods.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
          </div>
          <Field label="NOTES">
            <textarea data-testid="edit-notes" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="e.g., $100 check received 10/12, balance paid by Zelle 10/18" className={`${inputCls} resize-none`} />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gold/30 bg-cbtu-black/60">
          <button onClick={onClose} className="ghost-button px-4 py-2 rounded-sm text-xs tracking-widest font-heading">CANCEL</button>
          <button
            onClick={() => onSave({ ...f, total_paid: due, amount_received: received })}
            disabled={saving}
            data-testid="edit-payment-save"
            className="gold-button px-5 py-2 rounded-sm text-xs inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Save size={13} /> {saving ? 'SAVING…' : 'SAVE PAYMENT'}
          </button>
        </div>
      </div>
    </div>
  );
};
